import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Plug, Loader2, Check, ChevronDown } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { showToast, handleApiError, RateLimitInfo } from '../utils/errorHandler';
import RateLimitNotification from './RateLimitNotification';
import { cn } from '../lib/utils';
import { buttonVariants } from './ui/button';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Module-level cache so all AtsPushButton instances on the same page share one fetch.
const pushedCache: Record<string, { ids: Set<string>; promise?: Promise<void> }> = {};

interface AtsPushButtonProps {
  companyId: string;
  candidateUserId: string;
  cvId: string;
  t: (key: string, opts?: any) => string;
  className?: string;
  /** Legacy violet link-style (default) or RCC outline chip matching other row actions. */
  appearance?: 'violet' | 'outline';
  hideIcons?: boolean;
}

export default function AtsPushButton({
  companyId,
  candidateUserId,
  cvId,
  t,
  className = '',
  appearance = 'violet',
  hideIcons = false,
}: AtsPushButtonProps) {
  const { apiCall, user } = useAuth();
  const [connections, setConnections] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pushed, setPushed] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});
  const btnRef = useRef<HTMLButtonElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!companyId) return;
    const entry = pushedCache[companyId];
    if (entry) {
      if (entry.ids.has(candidateUserId)) setPushed(true);
      return;
    }
    pushedCache[companyId] = { ids: new Set() };
    pushedCache[companyId].promise = (async () => {
      try {
        const resp = await apiCall(`${apiBaseUrl}/api/v1/ats/pushed-candidates/${companyId}`);
        if (resp.ok) {
          const data = await resp.json();
          pushedCache[companyId].ids = new Set(data.candidate_ids || []);
          if (pushedCache[companyId].ids.has(candidateUserId)) setPushed(true);
        }
      } catch {}
    })();
  }, [companyId, candidateUserId, apiCall]);

  useEffect(() => {
    if (!companyId || !pushedCache[companyId]?.promise) return;
    pushedCache[companyId].promise!.then(() => {
      if (pushedCache[companyId]?.ids.has(candidateUserId)) setPushed(true);
    });
  }, [companyId, candidateUserId]);

  useEffect(() => {
    if (!open || connections.length > 0) return;
    (async () => {
      try {
        const [cResp, jResp] = await Promise.all([
          apiCall(`${apiBaseUrl}/api/v1/ats/connections/${companyId}`),
          apiCall(`${apiBaseUrl}/api/v1/ats/jobs/${companyId}`),
        ]);
        if (cResp.ok) setConnections((await cResp.json()).connections || []);
        if (jResp.ok) setJobs((await jResp.json()).jobs || []);
      } catch {}
    })();
  }, [open, companyId, apiCall, connections.length]);

  const positionDropdown = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const dropW = 240;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = rect.left;
    if (left + dropW > vw - 12) left = vw - dropW - 12;
    if (left < 12) left = 12;

    const spaceBelow = vh - rect.bottom;
    const openDown = spaceBelow > 80;
    const top = openDown ? rect.bottom + 6 : rect.top - 6;
    const transformOrigin = openDown ? 'top left' : 'bottom left';
    const anchor = openDown ? {} : { transform: 'translateY(-100%)' };

    setPortalStyle({
      position: 'fixed',
      top,
      left,
      width: Math.min(dropW, vw - 24),
      zIndex: 9999,
      transformOrigin,
      ...anchor,
    });
  }, []);

  useEffect(() => {
    if (open) positionDropdown();
  }, [open, positionDropdown]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    const onClick = (e: MouseEvent) => {
      if (
        btnRef.current?.contains(e.target as Node) ||
        dropRef.current?.contains(e.target as Node)
      ) return;
      setOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    document.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const handlePush = async (atsJobId: string, provider: string) => {
    if (!user?.userId) return;
    setPushing(true);
    try {
      const resp = await apiCall(`${apiBaseUrl}/api/v1/ats/push`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_id: companyId,
          provider,
          candidate_user_id: candidateUserId,
          cv_id: cvId,
          ats_job_id: atsJobId,
          pushed_by: user.userId,
        }),
      });
      if (resp.ok) {
        const data = await resp.json().catch(() => ({}));
        if (data?.warning) {
          showToast(data.warning, 'error');
        } else {
          showToast(t('atsPush.success'), 'success');
        }
        setPushed(true);
        if (pushedCache[companyId]) pushedCache[companyId].ids.add(candidateUserId);
        setOpen(false);
      } else if (resp.status === 429) {
        const apiError = await handleApiError(resp);
        if (apiError.rateLimitInfo) setRateLimitInfo(apiError.rateLimitInfo);
        setOpen(false);
      } else {
        const err = await resp.json().catch(() => ({}));
        const detail: string = typeof err?.detail === 'string' ? err.detail : t('atsPush.failed');
        if (detail.toLowerCase().includes('already pushed')) {
          showToast(t('atsPush.already_pushed'), 'error');
          setPushed(true);
        } else {
          showToast(detail, 'error');
        }
      }
    } catch {
      showToast(t('atsPush.failed'), 'error');
    } finally {
      setPushing(false);
    }
  };

  const dropdown = open
    ? ReactDOM.createPortal(
        <div
          ref={dropRef}
          style={portalStyle}
          className="bg-white rounded-xl shadow-xl border border-slate-200/80 p-1.5 space-y-0.5 animate-in fade-in zoom-in-95 duration-150"
        >
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2.5 pt-1.5 pb-1">{t('atsPush.select_job')}</p>
          {connections.length === 0 && (
            <p className="text-[11px] text-slate-400 px-2.5 py-3 text-center">{t('atsPush.no_connection')}</p>
          )}
          {connections.length > 0 && jobs.length === 0 && (
            <p className="text-[11px] text-slate-400 px-2.5 py-3 text-center">{t('atsPush.no_jobs')}</p>
          )}
          {jobs.map((job: any) => {
            const conn = connections.find((c: any) => c.id === job.connection_id);
            return (
              <button
                key={job.id}
                onClick={(e) => { e.stopPropagation(); handlePush(job.ats_job_id, conn?.provider || connections[0]?.provider); }}
                className={cn(
                  'w-full text-left px-2.5 py-2 rounded-lg transition-colors group',
                  appearance === 'outline'
                    ? 'hover:bg-slate-50 active:bg-slate-100'
                    : 'hover:bg-violet-50 active:bg-violet-100',
                )}
              >
                <span
                  className={cn(
                    'font-medium text-[11px] sm:text-xs text-gray-800 block truncate',
                    appearance === 'outline' ? 'group-hover:text-slate-900' : 'group-hover:text-violet-700',
                  )}
                >
                  {job.title}
                </span>
                {(job.department || job.location) && (
                  <span className="text-[10px] text-slate-400 block truncate">{[job.department, job.location].filter(Boolean).join(' · ')}</span>
                )}
              </button>
            );
          })}
        </div>,
        document.body,
      )
    : null;

  if (pushed) {
    if (appearance === 'outline') {
      return (
        <span
          className={cn(
            'inline-flex h-6 items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 text-xs font-medium text-emerald-700',
            className,
          )}
        >
          {!hideIcons && <Check className="size-3" />}
          {t('atsPush.already_pushed')}
        </span>
      );
    }
    return (
      <span className={`inline-flex items-center gap-1 px-2 sm:px-2.5 py-1.5 text-[11px] sm:text-xs font-semibold rounded-lg bg-green-50 text-green-600 ${className}`}>
        <Check className="w-3.5 h-3.5" /> {t('atsPush.already_pushed')}
      </span>
    );
  }

  return (
    <>
      {rateLimitInfo && (
        <RateLimitNotification rateLimitInfo={rateLimitInfo} feature="ats_push" onClose={() => setRateLimitInfo(null)} />
      )}
      <div className={`relative inline-block ${className}`}>
        <button
          ref={btnRef}
          onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
          disabled={pushing}
          className={cn(
            appearance === 'outline'
              ? buttonVariants({ variant: 'outline', size: 'xs', className: 'gap-0.5' })
              : 'inline-flex items-center gap-1 px-2 sm:px-2.5 py-1.5 text-[11px] sm:text-xs font-semibold rounded-lg text-violet-600 hover:text-violet-700 hover:bg-violet-50 transition-all disabled:opacity-50',
            className,
          )}
        >
          {!hideIcons && (pushing ? <Loader2 className="size-3 animate-spin" /> : <Plug className="size-3" />)}
          {pushing && hideIcons && <Loader2 className="size-3 animate-spin" />}
          {t('atsPush.button')}
          {!pushing && <ChevronDown className={cn('size-3 opacity-60 transition-transform', open && 'rotate-180')} aria-hidden />}
        </button>
        {dropdown}
      </div>
    </>
  );
}
